import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiError } from "@/lib/api";
import { Overview } from "@/components/staff/Overview";
import { Directory } from "@/components/staff/Directory";
import { Reviews } from "@/components/staff/Reviews";
import { Cases } from "@/components/staff/Cases";
import { Appeals } from "@/components/staff/Appeals";
import { Verification } from "@/components/staff/Verification";
import { Escalations, Overrides } from "@/components/staff/Escalations";
import { RulesQueue } from "@/components/rules/RulesQueue";
import { StaffDevApps } from "@/components/staff/StaffDevApps";
import { UsernameRequests } from "@/components/staff/UsernameRequests";
import { useAuth } from "@/context/AuthContext";
import { JUDGE_LABELS, ROLE_LABELS } from "@/lib/mtg";
import { PageHeader, Pager } from "@/components/common/PageParts";
import { JudgeBadge, RoleBadge } from "@/components/common/Badges";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function UsersTable() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const params = { q: q || undefined, page, page_size: 50 };
  const { data } = useQuery({ queryKey: ["staff", "users", params], queryFn: () => api.get("/staff/users", { params }).then((r) => r.data), placeholderData: keepPreviousData });

  const changeRole = async (u, role) => {
    const reason = window.prompt(`Reason for changing @${u.username} to ${ROLE_LABELS[role]} (recorded in the audit log, min 5 characters):`, "");
    if (!reason) return;
    try {
      await api.patch(`/staff/users/${u.user_id}/role`, { role, reason });
      toast.success(`@${u.username} is now ${ROLE_LABELS[role]}`);
      qc.invalidateQueries({ queryKey: ["staff"] });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setJudge = async (u, level) => {
    const reason = window.prompt(`Reason for ${level ? `making @${u.username} a ${JUDGE_LABELS[level]}` : `removing @${u.username}'s judge title`} (recorded in the audit log, min 5 characters):`, "");
    if (!reason) return;
    if (reason.trim().length < 5) { toast.error("Please give a reason of at least 5 characters"); return; }
    try {
      await api.patch(`/staff/users/${u.user_id}/judge-level`, { level: level || null, reason });
      toast.success(level ? `@${u.username} is now a ${JUDGE_LABELS[level]}` : `Judge title removed from @${u.username}`);
      qc.invalidateQueries({ queryKey: ["staff"] });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      <div className="relative mb-5 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
        <input className="field pl-9" placeholder="Search email, username or name" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search users" data-testid="staff-user-search" />
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="staff-users-table">
          <thead className="bg-[#1A202C] font-mono text-[10px] uppercase tracking-widest text-amber-300/80">
            <tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Change role</th><th className="px-4 py-3">Judge title</th></tr>
          </thead>
          <tbody>
            {data?.items.map((u) => (
              <tr key={u.user_id} className="border-t border-[#2A3447]" data-testid={`staff-user-row-${u.username}`}>
                <td className="px-4 py-3"><p className="text-slate-100">{u.display_name}</p><p className="font-mono text-xs text-slate-500">@{u.username}</p></td>
                <td className="px-4 py-3 text-slate-400">{u.email}</td>
                <td className="px-4 py-3"><div className="flex flex-wrap items-center gap-1"><RoleBadge role={u.role} /><JudgeBadge level={u.judge_level} /></div>{u.account_status !== "active" && <span className="ml-2 font-mono text-[10px] uppercase text-red-300" data-testid={`staff-user-status-${u.username}`}>{u.account_status}</span>}</td>
                <td className="px-4 py-3">
                  {u.assignable_roles.length ? (
                    <select className="field w-auto py-1.5 text-xs" value="" onChange={(e) => e.target.value && changeRole(u, e.target.value)} aria-label={`Change role for ${u.username}`} data-testid={`staff-role-select-${u.username}`}>
                      <option value="">Assign…</option>
                      {u.assignable_roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  ) : <span className="text-xs text-slate-600">—</span>}
                </td>
                <td className="px-4 py-3">
                  {u.can_set_judge ? (
                    <select className="field w-auto py-1.5 text-xs" value={u.judge_level || ""} onChange={(e) => setJudge(u, e.target.value)} aria-label={`Judge title for ${u.username}`} data-testid={`staff-judge-select-${u.username}`}>
                      <option value="">No judge title</option>
                      {Object.entries(JUDGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  ) : <span className="text-xs text-slate-600">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data && <Pager page={page} pageSize={50} total={data.total} onChange={setPage} testid="staff-users-pager" />}
    </>
  );
}

function AuditVerify() {
  const [res, setRes] = useState(null);
  const run = async () => { try { setRes((await api.get("/staff/audit-log/verify")).data); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="audit-verify">
      <button className="btn-outline text-xs" onClick={run} data-testid="audit-verify-button">Verify integrity</button>
      {res && <span className={`font-mono text-xs ${res.intact ? "text-emerald-300" : "text-red-300"}`} data-testid="audit-verify-result">
        {res.intact ? "Intact" : "PROBLEM DETECTED"} · {res.sealed_entries} sealed · {res.legacy_unsealed_entries} legacy · {res.tampered.length} tampered · {res.missing_sequence_numbers.length} gaps</span>}
    </div>
  );
}

function AuditLog() {
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const params = { page, action: action || undefined };
  const { data } = useQuery({ queryKey: ["staff", "audit", params], queryFn: () => api.get("/staff/audit-log", { params }).then((r) => r.data) });
  return (
    <>
      <p className="mb-4 text-sm text-slate-500">Append-only, HMAC-sealed and sequence-numbered record of staff actions. Entries cannot be edited or deleted; retained indefinitely.</p>
      {can("audit.verify") && <AuditVerify />}
      <input className="field mb-4 max-w-sm" placeholder="Filter by exact action (e.g. owner.override)" value={action} onChange={(e) => { setAction(e.target.value.trim()); setPage(1); }} data-testid="audit-action-filter" />
      <div className="panel overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="staff-audit-table">
          <thead className="bg-[#1A202C] font-mono text-[10px] uppercase tracking-widest text-amber-300/80">
            <tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Actor</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Target</th><th className="px-4 py-3">Details</th></tr>
          </thead>
          <tbody>
            {data?.items.map((a) => (
              <tr key={a.audit_id} className="border-t border-[#2A3447] align-top" data-testid={`audit-row-${a.audit_id}`}>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">@{a.actor_username} <RoleBadge role={a.actor_role} className="ml-1" /></td>
                <td className="px-4 py-3 font-mono text-xs text-amber-200">{a.action}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{a.target_type}:{a.target_id}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{a.reason && <p className="text-slate-300">{a.reason}</p>}{JSON.stringify(a.details)}{a.previous_state && <p>before {JSON.stringify(a.previous_state)} → after {JSON.stringify(a.new_state)}</p>}</td>
              </tr>
            ))}
            {data?.items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500" data-testid="audit-empty">No staff actions recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {data && <Pager page={page} pageSize={50} total={data.total} onChange={setPage} testid="staff-audit-pager" />}
    </>
  );
}

function TtsSettings() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["tts", "settings"], queryFn: () => api.get("/tts/settings").then((r) => r.data) });
  const { data: backs = [] } = useQuery({ queryKey: ["card-backs", "public-100"], queryFn: () => api.get("/card-backs", { params: { page_size: 100 } }).then((r) => r.data.items) });
  const save = async (id) => {
    try {
      await api.put("/tts/settings", { default_card_back_id: id || null });
      toast.success(id ? "Default TTS card back updated" : "Using the plain placeholder back");
      qc.invalidateQueries({ queryKey: ["tts", "settings"] });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="panel grid gap-6 p-6 sm:grid-cols-[160px_1fr]" data-testid="staff-tts-settings">
      {settings && <img src={assetUrl(settings.default_back_url)} alt="Current default card back" className="w-40 rounded-md border border-[#2A3447]" data-testid="staff-tts-back-preview" />}
      <div className="space-y-3 text-sm text-slate-300">
        <p className="eyebrow">Default Tabletop Simulator card back</p>
        <p>Used for every main-library card when a deck has no deck-level back. Only public card backs can be chosen, so every player at the table can load the image.</p>
        <select className="field max-w-sm" value={settings?.default_card_back_id || ""} onChange={(e) => save(e.target.value)} data-testid="staff-tts-back-select">
          <option value="">Plain placeholder back</option>
          {backs.map((b) => <option key={b.card_back_id} value={b.card_back_id}>{b.name}</option>)}
        </select>
        <p className="font-mono text-xs text-slate-500" data-testid="staff-tts-back-source">Source: {settings?.default_back_source}</p>
      </div>
    </div>
  );
}

export default function Staff() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const appeals = can("appeals.decide") || can("appeals.view") || can("appeals.final");
  const tabs = [
    ["overview", "Overview", true, <Overview />],
    ["directory", "Staff directory", true, <Directory />],
    ["users", "Users & roles", true, <UsersTable />],
    ["usernames", "Username requests", can("usernames.review"), <UsernameRequests />],
    ["reviews", "Application reviews", can("applications.view"), <Reviews />],
    ["reports", "Reports", can("reports.view"), <Cases initial={params.get("case")} />],
    ["appeals", "Appeals", appeals, <Appeals />],
    ["verification", "Verification", can("verification.view") || can("verification.spot_check"), <Verification />],
    ["rules", "Rules review", can("rulings.view") || can("rulings.review"), <RulesQueue />],
    ["developer", "Developer apps", can("developer.manage"), <StaffDevApps />],
    ["escalations", "Escalations", can("applications.escalation") || can("reports.view") || can("appeals.final"), <Escalations />],
    ["overrides", "Owner overrides", can("audit.read"), <Overrides />],
    ["audit", "Audit log", can("audit.read"), <AuditLog />],
    ["tts", "TTS export", can("site.settings"), <TtsSettings />],
  ].filter((t) => t[2]);
  const current = tabs.some((t) => t[0] === params.get("tab")) ? params.get("tab") : "overview";
  return (
    <div className="container-x py-12">
      <PageHeader eyebrow="Staff console" title="Trust & safety" testid="staff-header"
        description="Every action is permission-checked on the server and sealed in the audit log. You only see the tools your role allows." />
      <Tabs value={current} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="mb-8 h-auto flex-wrap justify-start bg-[#121620]">
          {tabs.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`staff-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([v, , , el]) => <TabsContent key={v} value={v}>{el}</TabsContent>)}
      </Tabs>
    </div>
  );
}
